/**
 * DB-driven marketing pixel loader.
 *
 * Fetches the enabled pixels from /api/site/pixels/ (managed in the dashboard's
 * Integrations panel) and injects them — Meta Pixel, GA4, Google Ads, TikTok, or
 * any custom snippet — with NO code changes needed to add a new tag.
 *
 * Also forwards SPA route changes and commerce events to whichever pixels loaded,
 * so Facebook/Google can optimise ad delivery and attribute conversions.
 *
 * Meta specifics (what the ads optimisation actually feeds on):
 *   - every commerce event carries contents / content_ids / content_type / value /
 *     currency / num_items in Meta's e-commerce shape;
 *   - Purchase carries an eventID ("purchase_<order_id>") that the server-side
 *     Conversions API sends too, so Meta de-duplicates the pair;
 *   - a signed-in customer's email / phone / name / id are passed to fbq('init') as
 *     advanced matching (the pixel hashes them before anything leaves the browser);
 *   - metaCookies() exposes _fbp / _fbc so checkout can hand them to the server.
 */
const API = process.env.VUE_APP_API_URL || "/api";
const PIXELS_URL = `${API.replace(/\/$/, "")}/site/pixels/`;

let loaded = [];
// Page views are sent manually by firePageView() (router.afterEach). If the first
// SPA page view fires before gtag is configured, remember it and flush on config.
let pendingGaPageView = false;
// Commerce events can fire before the pixels have loaded (a dish page's data often
// arrives before /site/pixels/ + fbevents.js on a cold load). Queue them until
// initPixels() has finished, then replay — otherwise ViewContent on a landing page
// (exactly what an ad click produces) would be lost.
let ready = false;
const pending = [];
const PENDING_CAP = 20;
// initMeta() sends the landing PageView itself; the router's afterEach for that same
// URL must not send a second one (it can arrive after the pixel loads on slow chunks).
let metaPageViewSentFor = "";

function injectScript(src, async = true) {
  const s = document.createElement("script");
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
  return s;
}

// Re-create <script> nodes from a raw HTML snippet so they actually execute.
function injectSnippet(html, placement) {
  try {
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const frag = tpl.content;
    const target = placement === "body_end" ? document.body : document.head;
    frag.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = old.textContent;
      old.replaceWith(s);
    });
    target.appendChild(frag);
  } catch (e) { /* noop */ }
}

function readCookie(name) {
  try {
    const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return m ? decodeURIComponent(m.pop()) : "";
  } catch (e) { return ""; }
}

/** The Meta browser identifiers the Conversions API needs to match a server event to
 *  this click: _fbp (set by the pixel) and _fbc (set when the visit carried an fbclid;
 *  rebuilt from the URL if the cookie isn't there yet). */
export function metaCookies() {
  const out = { fbp: readCookie("_fbp"), fbc: readCookie("_fbc") };
  try {
    if (!out.fbc) {
      const id = new URLSearchParams(window.location.search).get("fbclid");
      if (id) out.fbc = `fb.1.${Date.now()}.${id}`;
    }
  } catch (e) { /* noop */ }
  return out;
}

// Signed-in customer → Meta advanced matching (raw values; the pixel normalises + hashes
// client-side; nothing is sent in clear). Read from the app's stored user; never throws.
function metaAdvancedMatching() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return {};
    const am = {};
    if (u.email) am.em = String(u.email).trim().toLowerCase();
    const ph = String(u.mobile_number || u.phone || "").replace(/\D/g, "");
    if (ph) am.ph = ph.startsWith("0") && ph.length === 10 ? "61" + ph.slice(1) : ph;
    const name = String(u.full_name || u.name || "").trim().split(/\s+/).filter(Boolean);
    if (name[0]) am.fn = name[0].toLowerCase();
    if (name.length > 1) am.ln = name[name.length - 1].toLowerCase();
    if (u.id != null) am.external_id = String(u.id);
    return am;
  } catch (e) { return {}; }
}

function initMeta(id) {
  if (!id) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", id, metaAdvancedMatching());
  window.fbq("track", "PageView");
  try { metaPageViewSentFor = window.location.href; } catch (e) { /* noop */ }
}

function initGtag(id, isAds) {
  if (!id) return;
  if (!window.gtag) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
  }
  window.gtag("config", id, { send_page_view: false });
  if (pendingGaPageView) {
    pendingGaPageView = false;
    window.gtag("event", "page_view");
  }
  if (isAds) { /* conversions are sent explicitly on purchase */ }
}

function initTikTok(id) {
  if (!id) return;
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t; var ttq = (w[t] = w[t] || []);
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
    ttq.setAndDefer = function (e, n) { e[n] = function () { e.push([n].concat(Array.prototype.slice.call(arguments, 0))); }; };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.load = function (e) {
      var n = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = n; ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
      var o = d.createElement("script"); o.type = "text/javascript"; o.async = !0; o.src = n + "?sdkid=" + e;
      var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
    };
    ttq.load(id); ttq.page();
  })(window, document, "ttq");
  /* eslint-enable */
}

/** Fetch + inject all enabled pixels. Call once after mount. */
export async function initPixels() {
  if (typeof window === "undefined") return;
  try {
    const res = await fetch(PIXELS_URL, { mode: "cors", credentials: "omit" });
    if (!res.ok) return;
    const data = await res.json();
    loaded = data.pixels || [];
    loaded.forEach((p) => {
      try {
        if (p.provider === "meta_pixel") initMeta(p.pixel_id);
        else if (p.provider === "ga4") initGtag(p.pixel_id, false);
        else if (p.provider === "google_ads") initGtag(p.pixel_id, true);
        else if (p.provider === "tiktok") initTikTok(p.pixel_id);
        else if (p.provider === "custom" && p.snippet) injectSnippet(p.snippet, p.placement);
      } catch (e) { /* one bad pixel shouldn't break the rest */ }
    });
  } catch (e) { /* noop */ }
  finally {
    // fbq/gtag/ttq are queueing stubs from this point on, so replaying now is safe even
    // though their scripts may still be downloading. Also flush when nothing loaded, so
    // the queue can never grow unbounded.
    ready = true;
    pending.splice(0).forEach(([kind, opts]) => fireEvent(kind, opts));
  }
}

/** Forward an SPA page view to loaded pixels. */
export function firePageView() {
  try {
    if (window.fbq) {
      const href = window.location.href;
      if (href !== metaPageViewSentFor) {
        window.fbq("track", "PageView");
        metaPageViewSentFor = href;
      }
    }
  } catch (e) { /* noop */ }
  try {
    if (window.gtag) window.gtag("event", "page_view");
    else pendingGaPageView = true;
  } catch (e) { /* noop */ }
  try { if (window.ttq) window.ttq.page(); } catch (e) { /* noop */ }
}

const META_MAP = {
  product_view: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  complete_registration: "CompleteRegistration",
  search: "Search",
  subscribe: "Subscribe",
};
const GA_MAP = {
  product_view: "view_item",
  add_to_cart: "add_to_cart",
  begin_checkout: "begin_checkout",
  add_payment_info: "add_payment_info",
  purchase: "purchase",
  complete_registration: "sign_up",
  search: "search",
  subscribe: "subscribe",
};
const TT_MAP = {
  product_view: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "CompletePayment",
  complete_registration: "CompleteRegistration",
  search: "Search",
  subscribe: "Subscribe",
};

// GA4 `items` array from the line items a caller passes (cart lines or order lines).
function gaItems(items) {
  if (!Array.isArray(items) || !items.length) return undefined;
  return items.map((it) => ({
    item_id: it.item_id != null ? String(it.item_id) : undefined,
    item_name: it.item_name || undefined,
    price: it.price != null ? Number(it.price) : undefined,
    quantity: it.quantity != null ? Number(it.quantity) : 1,
  }));
}

// Meta `contents` from the same line items: [{id, quantity, item_price}].
function metaContents(items) {
  if (!Array.isArray(items) || !items.length) return undefined;
  return items
    .filter((it) => it.item_id != null)
    .map((it) => ({
      id: String(it.item_id),
      quantity: it.quantity != null ? Number(it.quantity) : 1,
      item_price: it.price != null ? Number(it.price) : undefined,
    }));
}

/**
 * Forward a commerce event to loaded pixels.
 * opts: { value, currency, items:[{item_id,item_name,price,quantity}], numItems, orderId,
 *         eventId, contentName, paymentType, searchString }
 */
export function fireEvent(kind, opts = {}) {
  if (!ready) {
    if (pending.length < PENDING_CAP) pending.push([kind, opts]);
    return;
  }
  const value = opts.value != null ? Number(opts.value) : undefined;
  const currency = opts.currency || "AUD";
  const items = gaItems(opts.items);
  const contents = metaContents(opts.items);
  try {
    if (window.fbq && META_MAP[kind]) {
      const p = { currency };
      if (value != null && !Number.isNaN(value)) p.value = value;
      if (contents && contents.length) {
        p.contents = contents;
        p.content_ids = contents.map((c) => c.id);
        p.content_type = "product";
      }
      if (opts.numItems != null) p.num_items = Number(opts.numItems);
      else if (contents && contents.length) p.num_items = contents.reduce((n, c) => n + (c.quantity || 1), 0);
      if (opts.contentName) p.content_name = String(opts.contentName);
      if (opts.searchString) p.search_string = String(opts.searchString);
      if (opts.paymentType) p.payment_type = String(opts.paymentType);
      if (opts.orderId && kind === "purchase") p.order_id = String(opts.orderId);
      if (opts.eventId) window.fbq("track", META_MAP[kind], p, { eventID: String(opts.eventId) });
      else window.fbq("track", META_MAP[kind], p);
    }
  } catch (e) { /* noop */ }
  try {
    if (window.gtag && GA_MAP[kind]) {
      const params = { value, currency, transaction_id: opts.orderId };
      if (items) params.items = items;
      if (opts.searchString) params.search_term = opts.searchString;
      if (opts.paymentType) params.payment_type = opts.paymentType;
      window.gtag("event", GA_MAP[kind], params);
    }
  } catch (e) { /* noop */ }
  try {
    if (window.ttq && TT_MAP[kind]) {
      const p = value != null ? { value, currency } : {};
      if (contents && contents.length) { p.contents = contents.map((c) => ({ content_id: c.id, quantity: c.quantity, price: c.item_price })); p.content_type = "product"; }
      window.ttq.track(TT_MAP[kind], p);
    }
  } catch (e) { /* noop */ }
}
